// "What a customer pays": the plan's settings applied to one amount of AWS usage, live as the form
// changes, with "Try another usage amount". The arithmetic and what it leaves out are in model.ts
// (`workedExample`); this is only its view.
import { useState } from "preact/hooks";
import { Field, InputAffix, errorId } from "@nexus/ui";
import { formatPercentBp } from "@nexus/ui";
import { Card } from "@nexus/ui";
import { Money } from "@nexus/ui";
import { DEFAULT_EXAMPLE_USAGE, percentLabel, workedExample, type PlanFormValues } from "./model";

function Line({ label, micro, sign, total }: { label: string; micro: bigint; sign?: "+" | "−"; total?: boolean }) {
  return (
    <div class={total ? "example-line example-total" : "example-line"}>
      <dt>{label}</dt>
      <dd>
        {sign && (
          <span aria-hidden="true" class="example-sign">
            {sign}{" "}
          </span>
        )}
        <Money micro={micro} />
      </dd>
    </div>
  );
}

export function WorkedExample({ values }: { values: PlanFormValues }) {
  const [usage, setUsage] = useState(DEFAULT_EXAMPLE_USAGE);
  const result = workedExample(values, usage);
  const basis = values.markupBasis === "actual" ? "AWS usage (actual bill)" : "AWS usage at list price";

  return (
    <Card title="What a customer pays" help="rateplan.workedExample" class="worked-example">
      <p class="muted">Live example with this plan's settings.</p>
      {result.kind === "ready" ? (
        <>
          <dl class="example-lines">
            <Line label={basis} micro={result.costMicro} />
            <Line label={`Markup ${percentLabel(result.markupBp)}`} micro={result.markupMicro} sign="+" />
            {result.discountMicro > 0n && <Line label="Volume discount" micro={result.discountMicro} sign="−" />}
            {result.topUpMicro > 0n && <Line label="Top-up to the monthly minimum" micro={result.topUpMicro} sign="+" />}
            <Line label="Platform fee" micro={result.feeMicro} sign="+" />
            <Line label="Invoice" micro={result.totalMicro} total />
            <div class="example-line example-profit">
              <dt>Our profit</dt>
              <dd>
                <span class="money-profit">
                  <Money micro={result.profitMicro} />
                </span>
                <span class="example-ratio">
                  {formatPercentBp(result.profitMarkupBp)} markup · {formatPercentBp(result.profitMarginBp)} margin
                </span>
              </dd>
            </div>
          </dl>
          {result.exceptionsLeftOut > 0 && (
            <p class="field-hint">
              Model exceptions are not included: {result.exceptionsLeftOut === 1 ? "1 exception prices" : `${result.exceptionsLeftOut} exceptions price`}{" "}
              some usage differently.
            </p>
          )}
        </>
      ) : (
        <p class="muted" role="status">
          {result.kind === "invalid-plan"
            ? "Fix the highlighted pricing fields to see the example."
            : "Type a dollar amount of usage to see the example."}
        </p>
      )}
      <Field
        id="example-usage"
        label="Try another usage amount"
        help="rateplan.exampleUsage"
        hint={values.markupBasis === "actual" ? "A month of AWS usage, as the actual AWS bill." : "A month of AWS usage, at AWS list price."}
        error={result.kind === "invalid-usage" ? "A dollar amount, zero or more." : null}
      >
        <InputAffix prefix="$">
          <input
            id="example-usage"
            class="input"
            inputMode="decimal"
            value={usage}
            aria-invalid={result.kind === "invalid-usage"}
            aria-describedby={result.kind === "invalid-usage" ? errorId("example-usage") : undefined}
            onInput={(e) => setUsage(e.currentTarget.value)}
          />
        </InputAffix>
      </Field>
    </Card>
  );
}
